import { useZero } from "@rocicorp/zero/react";
import { keyframes, styled } from "goober";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { isWebpageRoute } from "../../shared/stepConfig";
import type { StepExecutionItem } from "../../shared/executions/executionListTypes";
import { useDefaultConnectorGrants } from "../hooks/useDefaultConnectorGrants";
import { useHasAccess } from "../hooks/useHasAccess";
import { useNavigateOnBranchChange } from "../hooks/useNavigateOnBranchChange";
import { useQuery } from "../hooks/useQuery";
import { useSelectedExecution } from "../hooks/useSelectedExecution";
import {
  useAdjacentFailures,
  useStepExecutions,
} from "../hooks/useStepExecutions";
import { useTenantCustomDomain } from "../hooks/useTenantCustomDomain";
import { type ConnectorInfo, splitStepConnectors } from "../lib/connector";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import type {
  StreamingBashWrite,
  StreamingFileEdit,
} from "../lib/parseStreamingBashWrite";
import {
  getStepCopyableEmailAddress,
  getStepCopyableUrl,
} from "../lib/stepUrl";
import { publicDomain, publicUrl } from "../lib/tenantOrigin";
import { makeUrl, navigate, routes, useCurrentUrl } from "../lib/urls";
import { stepStorageVolumeName } from "../lib/workflowStoragePath";
import { zIndexes } from "../lib/zIndexes";
import { Button } from "./Button";
import type { CodeSelection } from "./CodeEditor";
import { ConnectorsBar } from "./ConnectorsBar";
import { Dropdown } from "./Dropdown";
import { executionNavTargets } from "./executionNavTargets";
import { Icon, type IconName } from "./Icon";
import { Link } from "./Link";
import { MetaSep } from "./MetaSeparator";
import { PlayButtons } from "./PlayButtons";
import { PressButton } from "./PressButton";
import { Resizable } from "./Resizable";
import {
  pinnedByExecutionId,
  type StepTestFixtureSetupRequest,
} from "../lib/pinnedExecutions";
import { LiveBrowser } from "./LiveBrowser";
import { useSettledStepContentKey } from "../lib/stepContentKey";
import { formatTime, StepExecutions } from "./StepExecutions";
import { type StepFileData, StepFiles } from "./StepFiles";
import { StepHeader } from "./StepHeader";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { WavyLine } from "./WavyLine";
import { maximizedStepPane } from "./maximizedStepPane";
import type {
  BranchData,
  CodeSnippet,
  ElementSnippet,
  StepData,
} from "./Workflow";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  height: 0;
  flex: 1;
`;

const Header = styled("div")`
  padding: 12px;
`;

const Body = styled("div")`
  display: flex;
  flex-direction: row;
  flex: 1;
  height: 0;
  padding: 0 12px;
`;

const ToggleButtonGroup = styled("div")`
  display: flex;
  align-items: center;
  border-radius: 9999px;
  position: relative;
  overflow: hidden;

  & button {
    background: none !important;
    border-radius: 0 !important;
  }

  > .shape {
    position: absolute;
    inset: 0;
    background: var(--alpha4);
    z-index: 0;
    transition: all 75ms ease-in-out;
    border-radius: 9999px;
    pointer-events: none;
  }

  > .line {
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 0.5px;
    background: var(--alpha8);
    transition: opacity 75ms ease-in-out;
    pointer-events: none;
  }

  &:hover > .line {
    opacity: 0;
  }

  &:has(button:hover) > .shape {
    background: var(--alpha8);
  }

  &:has(button[data-side="left"]:hover) > .shape {
    right: 50%;
  }

  &:has(button[data-side="right"]:hover) > .shape {
    left: 50%;
  }
`;

const ToggleButton = styled(Button)`
  &&& {
    border-radius: 0;
    background: transparent;
    position: relative;
    z-index: 1;
  }
  &.off span {
    opacity: 0.5;
  }
`;

const ExecutionNavRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const NavItem = styled("div")`
  position: relative;
  width: 24px;
  height: 24px;

  > .pill {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: ${zIndexes.dropdown};
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 2px;
    border-radius: 9999px;
    background: transparent;
    box-shadow: none;
  }

  /* The arrow's circle. Sized via a pseudo so it can scale (24px normally,
     20px once the drill-down container opens) without moving the 20px layout
     box that the container's 2px gaps depend on. */
  .arrowbtn::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    background: var(--alpha4);
    transform: scale(1.2);
    z-index: -1;
    transition:
      transform 120ms ease-in-out,
      background 120ms ease-in-out;
  }
  &:not([data-expanded]) .arrowbtn:hover::before {
    background: var(--alpha8);
  }

  /* Hidden (not just transparent) so the jump buttons stay out of the tab
     order until the container opens. */
  > .pill > .extra {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    visibility: hidden;
  }

  /* Open on hover (data-expanded) or keyboard focus so the jumps are reachable
     without a mouse. */
  &[data-expanded] > .pill,
  &:has(:focus-visible) > .pill {
    background: var(--surface-primary);
    box-shadow:
      0 0 0 0.5px var(--border-alpha),
      var(--elevation-low);
  }
  &[data-expanded] .arrowbtn::before,
  &:has(:focus-visible) .arrowbtn::before {
    transform: scale(1);
    background: var(--alpha4);
  }
  &[data-expanded] > .pill > .extra,
  &:has(:focus-visible) > .pill > .extra {
    max-height: 60px;
    opacity: 1;
    visibility: visible;
  }
`;

const navIconStyles = `
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  color: var(--text-primary);
  text-decoration: none;
`;

const NavIconLink = styled(Link)`
  ${navIconStyles}
`;

const NavIconPlaceholder = styled("span")`
  ${navIconStyles}
  pointer-events: none;
  opacity: 0.4;
`;

const flyoutStyles = `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  text-decoration: none;
  &:hover {
    background-color: var(--alpha8);
  }
`;

const FlyoutLink = styled(Link)`
  ${flyoutStyles}
`;

const FlyoutPlaceholder = styled("span")`
  ${flyoutStyles}
  pointer-events: none;
  opacity: 0.4;
`;

function ExecutionNavTarget({
  id,
  label,
  kind,
  className,
  children,
}: {
  id: string | undefined;
  label: string;
  kind: "arrow" | "flyout";
  className?: string;
  children: React.ReactNode;
}) {
  const target = id ? (
    kind === "arrow" ? (
      <NavIconLink
        className={className}
        to={makeUrl(null, { execution: id })}
        aria-label={label}
      >
        {children}
      </NavIconLink>
    ) : (
      <FlyoutLink
        className={className}
        to={makeUrl(null, { execution: id })}
        aria-label={label}
      >
        {children}
      </FlyoutLink>
    )
  ) : kind === "arrow" ? (
    <NavIconPlaceholder className={className} aria-hidden="true">
      {children}
    </NavIconPlaceholder>
  ) : (
    <FlyoutPlaceholder className={className} aria-hidden="true">
      {children}
    </FlyoutPlaceholder>
  );
  return (
    <Tooltip
      content={label}
      align={kind === "arrow" ? "center" : undefined}
      side={kind === "flyout" ? "left" : undefined}
    >
      {target}
    </Tooltip>
  );
}

const FileBrowserButton = styled(PressButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 9999px !important;
  border: none;
  cursor: pointer;
  color: var(--text-primary);
  background: var(--alpha4);
  &:hover {
    background: var(--alpha8);
  }
`;

const ExecutionLabel = styled(Text)`
  min-width: 0;
  flex: 1;
`;

const SplitButton = styled("div")`
  display: flex;
  align-items: center;
  border-radius: 9999px;
  position: relative;
  overflow: hidden;

  & button {
    background: none !important;
    border-radius: 0 !important;
    position: relative;
    z-index: 1;
  }

  & > div:first-child {
    width: 164px;
    flex-shrink: 0;

    div {
      width: 100%;
    }
  }

  > .shape {
    position: absolute;
    inset: 0;
    background: var(--alpha4);
    z-index: 0;
    transition: all 75ms ease-in-out;
    border-radius: 9999px;
    pointer-events: none;
  }

  > .line {
    position: absolute;
    left: 164px;
    top: 0;
    bottom: 0;
    width: 0.5px;
    background: var(--border-alpha);
    transition: opacity 75ms ease-in-out;
    pointer-events: none;
  }

  &:hover > .line {
    opacity: 0;
  }

  &:has(button[data-side="left"]:hover) > .shape {
    background: var(--alpha8);
    right: calc(100% - 164px);
  }

  &:has(button[data-side="right"]:hover) > .shape {
    background: var(--alpha8);
    left: 164px;
  }
`;

const SplitLeft = styled("button")`
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  padding: 4px 8px;
  height: 24px;
  width: 100%;
  cursor: pointer;
  min-width: 0;
  overflow: hidden;
`;

const SplitRight = styled(Button)`
  &&& {
    flex-shrink: 0;
  }
`;

const rotateFollowRing = keyframes`
  to { --follow-ring-angle: 360deg; }
`;

const PaneRing = styled("div")`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  width: 100%;
  height: 100%;

  &[data-follow-ring="true"]::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 12px;
    padding: 1.5px;
    box-sizing: border-box;
    pointer-events: none;
    z-index: 2;
    background: conic-gradient(
      from var(--follow-ring-angle),
      var(--pink-600) 0deg 180deg,
      var(--sky-600) 180deg 270deg,
      var(--orange-600) 270deg 313.2deg,
      var(--teal-600) 313.2deg 360deg
    );
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask-composite: exclude;
    animation: ${rotateFollowRing} 5s linear infinite;
  }
`;

let persistedPanelSize: number | undefined;

const NAV_EXPAND_DELAY_MS = 400;

function NavDirection({
  icon,
  runTooltip,
  successTooltip,
  failureTooltip,
  run,
  success,
  failureId,
}: {
  icon: IconName;
  runTooltip: string;
  successTooltip: string;
  failureTooltip: string;
  run: StepExecutionItem | undefined;
  success: StepExecutionItem | undefined;
  failureId: string | undefined;
}) {
  const [expanded, setExpanded] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The flyout must open even when the adjacent run is out of the loaded
  // window, so a failure reachable only through execution_failures stays
  // clickable.
  const hasContent = Boolean(run || success || failureId);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );
  const handleEnter = () => {
    if (!hasContent) return;
    timer.current = setTimeout(() => setExpanded(true), NAV_EXPAND_DELAY_MS);
  };
  const handleLeave = () => {
    if (timer.current) clearTimeout(timer.current);
    setExpanded(false);
  };
  return (
    <NavItem
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      data-expanded={(expanded || !run) && hasContent ? true : undefined}
    >
      <div className="pill">
        <ExecutionNavTarget
          id={run?.id}
          label={runTooltip}
          kind="arrow"
          className="arrowbtn"
        >
          <Icon name={icon} size={12} />
        </ExecutionNavTarget>
        {hasContent && (
          <div className="extra">
            <ExecutionNavTarget
              id={success?.id}
              label={successTooltip}
              kind="flyout"
            >
              <Icon name="check-filled" size={12} color="var(--green-600)" />
            </ExecutionNavTarget>
            <ExecutionNavTarget
              id={failureId}
              label={failureTooltip}
              kind="flyout"
            >
              <Icon name="check-filled-x" size={12} color="var(--red-600)" />
            </ExecutionNavTarget>
          </div>
        )}
      </div>
    </NavItem>
  );
}

function ExecutionNav({
  executions,
  selectedExecution,
  workflowId,
  branchId,
  stepDir,
}: {
  executions: StepExecutionItem[];
  selectedExecution: StepExecutionItem;
  workflowId: string;
  branchId: string;
  stepDir: string;
}) {
  const { older, newer, olderSuccess, newerSuccess } = executionNavTargets(
    executions,
    selectedExecution.id
  );
  // Failures cycle the whole retained history, not just the loaded window.
  const failures = useAdjacentFailures(workflowId, branchId, stepDir, {
    id: selectedExecution.id,
    createdAt: selectedExecution.createdAt,
  });
  return (
    <ExecutionNavRow>
      <NavDirection
        icon="arrow-down"
        runTooltip="Previous run"
        successTooltip="Previous successful run"
        failureTooltip="Previous failure"
        run={older}
        success={olderSuccess}
        failureId={failures.older?.id}
      />
      <NavDirection
        icon="arrow-up"
        runTooltip="Next run"
        successTooltip="Next successful run"
        failureTooltip="Next failure"
        run={newer}
        success={newerSuccess}
        failureId={failures.newer?.id}
      />
    </ExecutionNavRow>
  );
}

export function MaximizedStep({
  step,
  allSteps,
  spaceSlug,
  spaceId,
  allConnectors,
  branch,
  commitId,
  files,
  baseFiles,
  isReadOnly = false,
  canWrite,
  workflowPausedAt,
  onInvestigateFailure,
  onAskSetupStepTest,
  onSelectionChange,
  codeSnippets,
  isBeingWritten,
  forceShowCode = false,
  onStopFollow,
  expandOutput,
  followActive,
  streamingFileWrite,
  streamingFileEdit,
  onElementSelect,
  onElementDeselect,
  htmlInspectMode,
  onHtmlInspectModeChange,
  elementSnippets,
}: {
  step: StepData;
  allSteps: StepData[];
  spaceSlug: string;
  spaceId: string;
  allConnectors: readonly ConnectorInfo[];
  branch: BranchData;
  commitId: string;
  files: readonly StepFileData[];
  baseFiles?: readonly StepFileData[];
  isReadOnly?: boolean;
  canWrite: boolean;
  workflowPausedAt: number | null;
  onInvestigateFailure: (params: { stepDir: string; testName: string }) => void;
  onAskSetupStepTest: (request: StepTestFixtureSetupRequest) => void;
  onSelectionChange?: (selections: CodeSelection[]) => void;
  codeSnippets?: readonly CodeSnippet[];
  onElementSelect?: (
    element: Omit<ElementSnippet, "stepName" | "color">
  ) => void;
  onElementDeselect?: (index: number) => void;
  htmlInspectMode?: boolean;
  onHtmlInspectModeChange?: (mode: boolean) => void;
  elementSnippets?: readonly ElementSnippet[];
  isBeingWritten?: boolean;
  forceShowCode?: boolean;
  onStopFollow?: () => void;
  expandOutput?: boolean;
  followActive?: boolean;
  streamingFileWrite?: StreamingBashWrite | null;
  streamingFileEdit?: StreamingFileEdit | null;
}) {
  const z = useZero();
  const canCreateApp = useHasAccess("tenant:connected_app_create");
  const branchId = branch.id;
  const isOnMainBranch = branch.name === "main";
  const contentKey = useSettledStepContentKey(
    step.canonicalId,
    branch.headCommit?.files,
    branch.headCommitChat?.respondingAt != null
  );
  const navigateOnBranchChange = useNavigateOnBranchChange(branchId);
  const {
    workflowId,
    showRuns,
    execution: urlExecutionId,
  } = useCurrentUrl(routes.space.workflow);
  const defaultConnectorGrants = useDefaultConnectorGrants(spaceId);
  const customDomain = useTenantCustomDomain();

  const {
    connected: associatedConnectors,
    associatedIds: associatedConnectorIds,
    disconnected: disconnectedStepApps,
  } = useMemo(
    () => splitStepConnectors(step.connectedApps ?? [], allConnectors),
    [step.connectedApps, allConnectors]
  );

  const associatedConnectorIdSet = useMemo(
    () => new Set(associatedConnectorIds),
    [associatedConnectorIds]
  );
  // allConnectors is already filtered to what this space can use, so the picker
  // offers only those — labeled "Available to this space", or "Available to
  // you" for a personal space (which the UI never calls a "space").
  const spaceUsableConnectorIds = useMemo(
    () => new Set(allConnectors.map((app) => app.id)),
    [allConnectors]
  );
  const [personalSpace] = useQuery(queries.personalSpace());
  const availableConnectorLabel =
    spaceId === personalSpace?.id
      ? "Available to you"
      : "Available to this space";
  const { executions } = useStepExecutions(workflowId, branchId, step.id);
  const pendingBuildKeys = useMemo(
    () =>
      new Set(
        executions
          .filter((e) => e.kind === "build" && e.status === "pending")
          .map((e) => e.buildKey)
      ),
    [executions]
  );
  const { selected: selectedExecution } = useSelectedExecution(
    step.id,
    workflowId,
    branchId
  );
  const storageVolumeName = useMemo(() => {
    const dockerfile = files.find((f) => f.path === "Dockerfile");
    return stepStorageVolumeName(dockerfile?.content);
  }, [files]);
  const pinnedByExecId = useMemo(() => pinnedByExecutionId(files), [files]);
  const hasStorage = storageVolumeName !== null;

  const handleReEmit = useCallback(
    (executionId: string) => {
      z.mutate(
        sharedMutators.reEmitRetainedExecution({
          executionId,
          newExecutionId: nanoid(12),
          workflowId,
        })
      );
    },
    [workflowId, z]
  );

  const browserApplicable = isWebpageRoute(step.routeType);
  const effectiveShowBrowser =
    maximizedStepPane(browserApplicable, urlExecutionId) === "browser";

  const toDropdownItem = (execution: (typeof executions)[number]) => {
    const isQueued =
      execution.kind === "run" &&
      execution.status === "pending" &&
      pendingBuildKeys.has(execution.buildKey);
    return {
      type: "link" as const,
      key: execution.id,
      selected: !effectiveShowBrowser && execution.id === selectedExecution?.id,
      label:
        execution.kind === "build" && execution.status === "pending"
          ? "Building…"
          : execution.kind === "build"
            ? (execution.run?.commit?.message ??
              formatTime(execution.createdAt))
            : formatTime(execution.createdAt),
      subtitle: (
        <Text variant="text-small" ellipsis>
          <span
            style={{
              color:
                execution.status === "success"
                  ? "var(--green-600)"
                  : execution.status === "error"
                    ? "var(--red-600)"
                    : "var(--text-secondary)",
            }}
          >
            {isQueued
              ? "Queued"
              : execution.status === "pending"
                ? "Running"
                : execution.status === "success"
                  ? "Successful"
                  : "Failed"}
          </span>
          <span style={{ color: "var(--text-secondary)" }}>
            <MetaSep />
            {formatRelativeTime(execution.createdAt)}
          </span>
        </Text>
      ),
      muted: false,
      title:
        execution.kind === "build" && execution.status !== "pending"
          ? (execution.run?.commit?.message ?? undefined)
          : undefined,
      left:
        execution.kind === "build" && execution.status === "pending" ? (
          <WavyLine size={16} opacity={1} />
        ) : execution.kind === "build" ? (
          <Tooltip content="Build">
            <Icon name="package-2" size={16} color="var(--text-primary)" />
          </Tooltip>
        ) : isQueued ? (
          <Icon name="clock" size={16} />
        ) : pinnedByExecId.has(execution.id) ? (
          <Tooltip content="Step test">
            <Icon name="microscope" size={16} />
          </Tooltip>
        ) : (
          <Icon
            name={execution.run?.ephemeral ? "solo-run" : "run"}
            size={16}
          />
        ),
      right:
        !effectiveShowBrowser && execution.id === selectedExecution?.id ? (
          <Icon name="check" size={12} color="var(--text-secondary)" />
        ) : undefined,
      to: makeUrl(null, {
        execution: execution.id,
      }),
    };
  };

  const browserItem = {
    type: "link" as const,
    key: "__browser",
    label: "Browser",
    subtitle: (
      <Text variant="text-small" color="var(--text-secondary)" ellipsis>
        View and edit the latest build
      </Text>
    ),
    muted: false,
    left: <Icon name="earth" size={12} />,
    right: effectiveShowBrowser ? (
      <Icon name="check" size={12} color="var(--text-secondary)" />
    ) : undefined,
    to: makeUrl(null, { execution: "" }),
  };

  const executionSections = [
    {
      label: undefined,
      sticky: true,
      items: browserApplicable ? [browserItem] : [],
    },
    {
      label: undefined,
      items: executions.map(toDropdownItem),
    },
  ].filter((s) => s.items.length > 0);

  const bodyRef = useRef<HTMLDivElement>(null);
  const splitButtonAnchor = useRef(`--split-button-${nanoid()}`);

  const associateApp = (connectedAppId: string, slug?: string) =>
    navigateOnBranchChange((branchParams) =>
      z.mutate(
        sharedMutators.associateStepWithConnectedApp({
          ...branchParams,
          stepDir: step.name,
          connectedAppId,
          ...(slug && { slug }),
        })
      )
    );

  const disassociateApp = (connectedAppId: string) => {
    void navigateOnBranchChange((branchParams) =>
      z.mutate(
        sharedMutators.dissociateStepFromConnectedApp({
          ...branchParams,
          stepDir: step.name,
          connectedAppId,
        })
      )
    );
  };

  const handlePanelResize = useCallback((size: number | undefined) => {
    persistedPanelSize = size;
  }, []);

  const toggleRuns = () => navigate(null, { showRuns: showRuns ? "" : "true" });

  const [preferences] = useQuery(queries.userPreferences());
  const persistedShowCode = preferences?.showCode !== "off";

  const showCode = forceShowCode || persistedShowCode;
  const toggleCode = () => {
    if (showCode && forceShowCode) {
      onStopFollow?.();
    }
    z.mutate(
      sharedMutators.setUserPreference({
        showCode: showCode ? "off" : "on",
      })
    );
  };

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey) return;
      if (e.key === "!" || e.key === "1") {
        e.preventDefault();
        toggleCode();
      } else if (e.key === "@" || e.key === "2") {
        e.preventDefault();
        toggleRuns();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  });

  const externalPublicUrl = publicUrl();
  const domain = publicDomain();
  const stepUrl = getStepCopyableUrl(
    step,
    branchId,
    isOnMainBranch,
    spaceSlug,
    externalPublicUrl,
    domain,
    customDomain
  );
  const stepEmail = getStepCopyableEmailAddress(
    step,
    branchId,
    isOnMainBranch,
    spaceSlug,
    domain
  );

  return (
    <Container>
      <Header>
        <StepHeader
          step={step}
          email={stepEmail}
          onBack={() =>
            navigate(
              routes.space.workflow,
              { spaceId, workflowId, step: "", file: "" },
              { replace: true }
            )
          }
          right={
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {!isReadOnly && (
                <ConnectorsBar
                  parentNoun="step"
                  canWrite={canWrite}
                  canCreateApp={canCreateApp}
                  defaultConnectorGrants={defaultConnectorGrants}
                  connected={associatedConnectors}
                  excludeIds={associatedConnectorIdSet}
                  availableIds={spaceUsableConnectorIds}
                  availableLabel={availableConnectorLabel}
                  disconnected={disconnectedStepApps.map((sca) => ({
                    id: sca.slug,
                    appType: sca.appType,
                    slug: sca.slug,
                  }))}
                  associate={associateApp}
                  remove={disassociateApp}
                  afterCreate={(newAppId) => {
                    navigate(routes.space.workflow, {
                      spaceId,
                      workflowId,
                      step: step.canonicalId,
                    });
                    navigate(routes.modal.connector, { id: newAppId });
                  }}
                />
              )}
              {hasStorage && (
                <Tooltip content="Browse storage">
                  <FileBrowserButton
                    onPress={() =>
                      navigate(routes.modal.storage, {
                        spaceId,
                        branchId,
                        volume: storageVolumeName,
                      })
                    }
                  >
                    <Icon name="box" size={12} />
                  </FileBrowserButton>
                </Tooltip>
              )}
              <ToggleButtonGroup>
                <div className="shape" />
                <div className="line" />
                <Tooltip
                  align="center"
                  content={
                    <>
                      {showCode ? "Hide code" : "Show code"}{" "}
                      <span
                        style={{
                          fontSize: 10,
                          color: "var(--tooltip-fg-muted)",
                          marginLeft: 4,
                        }}
                      >
                        ⌘⇧1
                      </span>
                    </>
                  }
                >
                  <ToggleButton
                    icon="code"
                    variant="secondary"
                    className={showCode ? "on" : "off"}
                    onMouseDown={toggleCode}
                    data-side="left"
                  />
                </Tooltip>
                <Tooltip
                  align="center"
                  content={
                    <>
                      {showRuns ? "Hide runs" : "Show runs"}{" "}
                      <span
                        style={{
                          fontSize: 10,
                          color: "var(--tooltip-fg-muted)",
                          marginLeft: 4,
                        }}
                      >
                        ⌘⇧2
                      </span>
                    </>
                  }
                >
                  <ToggleButton
                    icon="runs"
                    variant="secondary"
                    className={showRuns ? "on" : "off"}
                    onMouseDown={toggleRuns}
                    data-side="right"
                  />
                </Tooltip>
              </ToggleButtonGroup>
              {(effectiveShowBrowser || selectedExecution) && (
                <SplitButton
                  style={
                    {
                      // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
                      anchorName: splitButtonAnchor.current,
                    } as React.CSSProperties
                  }
                >
                  <Dropdown
                    align="bottom"
                    minWidth={280}
                    target={
                      !effectiveShowBrowser &&
                      (step.links ?? []).length > 0 &&
                      selectedExecution?.status === "success"
                        ? // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
                          splitButtonAnchor.current
                        : undefined
                    }
                    opener={
                      <SplitLeft data-side="left">
                        {effectiveShowBrowser || !selectedExecution ? (
                          <>
                            <span style={{ display: "flex", flexShrink: 0 }}>
                              <Icon name="earth" size={12} />
                            </span>
                            <ExecutionLabel variant="text-small-md" ellipsis>
                              Browser
                            </ExecutionLabel>
                          </>
                        ) : (
                          <>
                            <span style={{ display: "flex", flexShrink: 0 }}>
                              {selectedExecution.kind === "build" &&
                              selectedExecution.status === "pending" ? (
                                <WavyLine size={12} opacity={1} />
                              ) : (
                                <Icon
                                  name={
                                    selectedExecution.kind === "build"
                                      ? "package-2"
                                      : pinnedByExecId.has(selectedExecution.id)
                                        ? "microscope"
                                        : selectedExecution.run?.ephemeral
                                          ? "solo-run"
                                          : "run"
                                  }
                                  size={12}
                                />
                              )}
                            </span>
                            <ExecutionLabel variant="text-small-md" ellipsis>
                              {selectedExecution.kind === "build" &&
                              selectedExecution.status === "pending"
                                ? "Building…"
                                : selectedExecution.kind === "build"
                                  ? (selectedExecution.run?.commit?.message ??
                                    formatTime(selectedExecution.createdAt))
                                  : formatTime(selectedExecution.createdAt)}
                            </ExecutionLabel>
                          </>
                        )}
                        <Icon
                          name="chevron-down"
                          size={12}
                          style={{ flexShrink: 0, marginLeft: "auto" }}
                        />
                      </SplitLeft>
                    }
                    sections={executionSections}
                  />
                  {!effectiveShowBrowser &&
                    selectedExecution &&
                    (step.links ?? []).length > 0 &&
                    selectedExecution.status === "success" && (
                      <Tooltip content="Re-emit">
                        <SplitRight
                          icon="refresh-cw"
                          variant="secondary"
                          onMouseDown={() => handleReEmit(selectedExecution.id)}
                          data-side="right"
                        />
                      </Tooltip>
                    )}
                  <div className="shape" />
                  {!effectiveShowBrowser &&
                    selectedExecution &&
                    (step.links ?? []).length > 0 &&
                    selectedExecution.status === "success" && (
                      <div className="line" />
                    )}
                </SplitButton>
              )}
              {!effectiveShowBrowser &&
                selectedExecution &&
                executions.length > 1 && (
                  <ExecutionNav
                    executions={executions}
                    selectedExecution={selectedExecution}
                    workflowId={workflowId}
                    branchId={branchId}
                    stepDir={step.id}
                  />
                )}
              {!isReadOnly && (
                <PlayButtons
                  onExecution={(id) => navigate(null, { execution: id })}
                  step={step}
                  commitId={commitId}
                  branchId={branchId}
                  disabled={!canWrite}
                  disabledReason={
                    isOnMainBranch && workflowPausedAt != null
                      ? "Workflow is paused"
                      : undefined
                  }
                />
              )}
            </div>
          }
        />
      </Header>
      <Body ref={bodyRef}>
        {(() => {
          const rightPane = effectiveShowBrowser ? (
            <LiveBrowser
              step={step}
              spaceSlug={spaceSlug}
              spaceId={spaceId}
              branchId={branchId}
              contentKey={contentKey}
              navBranchId={isOnMainBranch ? undefined : branchId}
              displayUrl={(stepUrl ?? "").replace(/^https?:\/\//, "")}
              openUrl={stepUrl ?? undefined}
              inspectMode={htmlInspectMode}
              onInspectModeChange={onHtmlInspectModeChange}
              onElementSelect={onElementSelect}
              onElementDeselect={onElementDeselect}
              elementSnippets={elementSnippets}
            />
          ) : (
            <StepExecutions
              step={step}
              branchId={branchId}
              commitId={commitId}
              files={files}
              isMainBranch={isOnMainBranch}
              pinnedByExecId={pinnedByExecId}
              onSelectionChange={onSelectionChange}
              codeSnippets={codeSnippets}
              isBeingWritten={isBeingWritten}
              onInvestigateFailure={onInvestigateFailure}
              onAskSetupStepTest={onAskSetupStepTest}
            />
          );
          if (!showCode) return rightPane;
          return (
            <Resizable
              minSize={300}
              defaultSize="calc(50% - 6px)"
              initialSize={persistedPanelSize}
              onSizeChange={handlePanelResize}
              Handle={
                <div
                  style={{
                    width: 12,
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    data-drag-indicator
                    style={{
                      height: 32,
                      width: 2,
                      background: "var(--border-alpha)",
                      borderRadius: 10,
                    }}
                  />
                </div>
              }
              containerRef={bodyRef}
              orientation="horizontal"
              first={
                <PaneRing
                  data-follow-ring={
                    followActive && !expandOutput ? "true" : undefined
                  }
                >
                  <StepFiles
                    files={files}
                    previousFiles={baseFiles}
                    canShowDiff={baseFiles != null}
                    stepName={step.name}
                    allStepNames={allSteps.map((s) => s.name)}
                    onSelectionChange={onSelectionChange}
                    codeSnippets={codeSnippets}
                    streamingFileWrite={streamingFileWrite ?? null}
                    streamingFileEdit={streamingFileEdit ?? null}
                  />
                </PaneRing>
              }
              second={
                <PaneRing
                  data-follow-ring={
                    followActive && expandOutput ? "true" : undefined
                  }
                >
                  {rightPane}
                </PaneRing>
              }
            />
          );
        })()}
      </Body>
    </Container>
  );
}
